/**
 * The days a shift or a series collides on (#2119, decision D4): another shift of the employee
 * or an approved absence. Shown by the create dialog before the save (inline check of every
 * day) and after a 409, and by the edit dialog after a 409. For a series with free days left
 * it offers to create the series without the colliding days.
 */
import { useLingui } from '@lingui/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { ApiError } from '@/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { formatDayList } from '@/hooks/shiftSeriesToast';
import { extractBackendError } from '@/lib/translateBackendError';
import type { ShiftConflictErrorResponse } from '@/types/shifts';

export interface ShiftCollision {
  /** Days with another shift of the employee (YYYY-MM-DD). */
  overlapDates: string[];
  /** Days with an approved absence (YYYY-MM-DD). */
  absentDates: string[];
}

/**
 * The collision a failed save answered: the 409 codes SHIFT_CONFLICT and EMPLOYEE_ABSENT. A
 * create names its days (conflict_dates); an update names none, so fallbackDate fills in.
 */
export function collisionFromError(error: unknown, fallbackDate?: string): ShiftCollision | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = extractBackendError(error) as ShiftConflictErrorResponse;
  if (body.code !== 'SHIFT_CONFLICT' && body.code !== 'EMPLOYEE_ABSENT') return null;
  const dates = body.conflict_dates?.length
    ? body.conflict_dates
    : body.conflict_date
      ? [body.conflict_date]
      : fallbackDate
        ? [fallbackDate]
        : [];
  return body.code === 'SHIFT_CONFLICT'
    ? { overlapDates: dates, absentDates: [] }
    : { overlapDates: [], absentDates: dates };
}

export function hasCollision(collision: ShiftCollision | null | undefined): collision is ShiftCollision {
  return !!collision && collision.overlapDates.length + collision.absentDates.length > 0;
}

/**
 * One person's colliding days when the dialog plans several people (#2121). The inline check
 * tells overlaps from absences; a 409 names the days only (`days`).
 */
export interface PersonCollision {
  employeeId: string;
  overlapDates: string[];
  absentDates: string[];
  days: string[];
}

const collidingCount = (person: PersonCollision) =>
  person.overlapDates.length + person.absentDates.length + person.days.length;

/** The (employee, days) pairs of a multi-employee 409 (`conflicts`). */
export function peopleCollisionsFromError(error: unknown): PersonCollision[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = extractBackendError(error) as ShiftConflictErrorResponse;
  if (body.code !== 'SHIFT_CONFLICT' && body.code !== 'EMPLOYEE_ABSENT') return null;
  if (!body.conflicts?.length) return null;
  return body.conflicts.map((pair) => ({
    employeeId: pair.employee_id,
    overlapDates: [],
    absentDates: [],
    days: pair.dates,
  }));
}

export interface PeopleCollisionAlertProps {
  people: PersonCollision[];
  /** First name per employee id. */
  nameOf: (employeeId: string) => string;
  /** Shifts the save would create without any collision (people x days). */
  totalShifts: number;
  /** Creates everything except the colliding pairs. */
  onSkip: () => void;
  isPending?: boolean;
}

/** The colliding (person, days) pairs of a multi-employee create (#2121, M3). */
export function PeopleCollisionAlert({ people, nameOf, totalShifts, onSkip, isPending = false }: PeopleCollisionAlertProps) {
  const { i18n } = useLingui();
  const colliding = people.reduce((sum, person) => sum + collidingCount(person), 0);
  const remaining = totalShifts - colliding;
  return (
    <Alert variant="warning" data-testid="shift-collision-alert">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>
        {i18n._({
          id: 'shifts.collision.titlePeople',
          message: '{count, plural, one {# shift collides} other {# shifts collide}}',
          values: { count: colliding },
        })}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <ul className="space-y-0.5">
          {people.map((person) => {
            const name = nameOf(person.employeeId) || i18n._({ id: 'shifts.collision.theEmployee', message: 'The employee' });
            return (
              <li key={person.employeeId} data-testid={`collision-person-${person.employeeId}`}>
                {person.overlapDates.length > 0 &&
                  i18n._({
                    id: 'shifts.collision.overlapDays',
                    message: '{name} already has a shift at this time on {days}.',
                    values: { name, days: formatDayList(person.overlapDates, i18n.locale) },
                  })}
                {person.overlapDates.length > 0 && person.absentDates.length > 0 && ' '}
                {person.absentDates.length > 0 &&
                  i18n._({
                    id: 'shifts.collision.absentDays',
                    message: '{name} is absent (approved) on {days}.',
                    values: { name, days: formatDayList(person.absentDates, i18n.locale) },
                  })}
                {person.days.length > 0 &&
                  i18n._({
                    id: 'shifts.collision.personDays',
                    message: '{name} already works or is absent on {days}.',
                    values: { name, days: formatDayList(person.days, i18n.locale) },
                  })}
              </li>
            );
          })}
        </ul>
        {remaining > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-auto min-h-9 whitespace-normal"
              onClick={onSkip}
              disabled={isPending}
              data-testid="skip-colliding-days"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {i18n._({ id: 'shifts.collision.skip', message: 'Skip colliding days' })}
            </Button>
            <span className="text-xs">
              {i18n._({
                id: 'shifts.collision.skipHintPeople',
                message:
                  '{count, plural, one {Creates the # remaining shift; a person without a free day gets none.} other {Creates the # remaining shifts; a person without a free day gets none.}}',
                values: { count: remaining },
              })}
            </span>
          </div>
        ) : (
          <p>
            {i18n._({
              id: 'shifts.collision.allPeople',
              message: 'Every shift collides. Change the time, the date or the people.',
            })}
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

export interface ShiftCollisionAlertProps {
  collision: ShiftCollision;
  /** The employee's first name for the sentence; empty falls back to "The employee". */
  employeeName: string;
  /** Number of days of the series; skipping is offered when some days stay free. */
  totalDays: number;
  /** Creates the series without the colliding days; absent for a single shift. */
  onSkip?: () => void;
  isPending?: boolean;
}

export function ShiftCollisionAlert({
  collision,
  employeeName,
  totalDays,
  onSkip,
  isPending = false,
}: ShiftCollisionAlertProps) {
  const { i18n } = useLingui();
  const colliding = collision.overlapDates.length + collision.absentDates.length;
  const canSkip = !!onSkip && totalDays > 1 && colliding < totalDays;
  const allCollide = totalDays > 1 && colliding >= totalDays;
  const name = employeeName || i18n._({ id: 'shifts.collision.theEmployee', message: 'The employee' });

  return (
    <Alert variant="warning" data-testid="shift-collision-alert">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>
        {totalDays > 1
          ? i18n._({
              id: 'shifts.collision.titleSeries',
              message: '{count, plural, one {# day of the series collides} other {# days of the series collide}}',
              values: { count: colliding },
            })
          : i18n._({ id: 'shifts.conflict.warning', message: 'Conflict Detected' })}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        {collision.overlapDates.length > 0 && (
          <p data-testid="collision-overlap-days">
            {i18n._({
              id: 'shifts.collision.overlapDays',
              message: '{name} already has a shift at this time on {days}.',
              values: { name, days: formatDayList(collision.overlapDates, i18n.locale) },
            })}
          </p>
        )}
        {collision.absentDates.length > 0 && (
          <p data-testid="collision-absent-days">
            {i18n._({
              id: 'shifts.collision.absentDays',
              message: '{name} is absent (approved) on {days}.',
              values: { name, days: formatDayList(collision.absentDates, i18n.locale) },
            })}
          </p>
        )}
        {allCollide && (
          <p>
            {i18n._({
              id: 'shifts.collision.allDays',
              message: 'Every day of the series collides. Change the time, the date or the employee.',
            })}
          </p>
        )}
        {canSkip && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-auto min-h-9 whitespace-normal"
              onClick={onSkip}
              disabled={isPending}
              data-testid="skip-colliding-days"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {i18n._({ id: 'shifts.collision.skip', message: 'Skip colliding days' })}
            </Button>
            <span className="text-xs">
              {i18n._({
                id: 'shifts.collision.skipHint',
                message:
                  '{count, plural, one {Creates the series with the # remaining shift.} other {Creates the series with the # remaining shifts.}}',
                values: { count: totalDays - colliding },
              })}
            </span>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
